import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const InterventionDetails = ({ appointment, onComplete, onUpdatePhotos, onUpdateSignature }) => {
  const [activeTab, setActiveTab] = useState('checklist');
  const [checklistItems, setChecklistItems] = useState([
    { id: 1, label: 'Vérification de la fuite principale', checked: false, required: true },
    { id: 2, label: 'Inspection des joints et raccords', checked: false, required: true },
    { id: 3, label: 'Test de pression du système', checked: false, required: true },
    { id: 4, label: 'Nettoyage de la zone de travail', checked: false, required: false },
    { id: 5, label: 'Explication au client des travaux effectués', checked: false, required: true }
  ]);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [availableConsumables, setAvailableConsumables] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [signature, setSignature] = useState(null);
  const [notes, setNotes] = useState('');
  const [isLoadingConsumables, setIsLoadingConsumables] = useState(true);

  const tabs = [
    { id: 'checklist', label: 'Checklist', icon: 'ClipboardCheck' },
    { id: 'photos', label: 'Photos', icon: 'Camera' },
    { id: 'consumables', label: 'Consommables', icon: 'Package' },
    { id: 'signature', label: 'Signature', icon: 'PenTool' }
  ];

  // Load available consumables from database
  useEffect(() => {
    loadConsumables();
  }, []);

  const loadConsumables = async () => {
    try {
      setIsLoadingConsumables(true);
      const { data, error } = await supabase
        ?.from('consumables')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('name');

      if (error) throw error;

      const consumablesWithQuantity = data?.map(item => ({
        id: item?.id,
        name: item?.name,
        quantity: 0,
        unit: item?.unit,
        currentStock: item?.current_stock,
        minimumThreshold: item?.minimum_threshold
      }));

      setAvailableConsumables(consumablesWithQuantity || []);
      setConsumables(consumablesWithQuantity || []);
    } catch (err) {
      console.error('Error loading consumables:', err);
    } finally {
      setIsLoadingConsumables(false);
    }
  };

  const handleChecklistToggle = (id) => {
    setChecklistItems(items =>
      items?.map(item =>
        item?.id === id ? { ...item, checked: !item?.checked } : item
      )
    );
  };

  const handlePhotoUpload = (type, event) => {
    const files = Array.from(event?.target?.files);
    const photoUrls = files?.map(file => URL.createObjectURL(file));
    
    if (type === 'before') {
      setBeforePhotos(prev => [...prev, ...photoUrls]);
    } else {
      setAfterPhotos(prev => [...prev, ...photoUrls]);
    }
  };

  const handleConsumableChange = (id, value) => {
    setConsumables(items =>
      items?.map(item =>
        item?.id === id ? { ...item, quantity: parseInt(value) || 0 } : item
      )
    );
  };

  const canComplete = () => {
    const requiredChecked = checklistItems?.filter(item => item?.required)?.every(item => item?.checked);
    return requiredChecked && beforePhotos?.length > 0 && afterPhotos?.length > 0 && signature;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm h-full flex flex-col">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Intervention en cours</h2>
            <p className="text-sm text-muted-foreground">{appointment?.customerName}</p>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Début: {new Date(appointment?.startTime || Date.now())?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'checklist' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Points de contrôle</h3>
              <span className="text-sm text-muted-foreground">
                {checklistItems?.filter(item => item?.checked)?.length}/{checklistItems?.length} complétés
              </span>
            </div>
            {checklistItems?.map(item => (
              <div key={item?.id} className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Checkbox
                  checked={item?.checked}
                  onChange={() => handleChecklistToggle(item?.id)}
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {item?.label}
                    {item?.required && <span className="text-error ml-1">*</span>}
                  </label>
                </div>
              </div>
            ))}
            <div className="mt-4">
              <Input
                label="Notes d'intervention"
                type="text"
                placeholder="Ajoutez des notes sur l'intervention..."
                value={notes}
                onChange={(e) => setNotes(e?.target?.value)}
                className="w-full"
              />
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-foreground">Photos avant intervention</h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload('before', e)}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" iconName="Plus" asChild>
                    <span>Ajouter</span>
                  </Button>
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {beforePhotos?.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={photo}
                      alt={`Photo avant intervention numéro ${index + 1} montrant l'état initial du problème de plomberie`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {beforePhotos?.length === 0 && (
                  <div className="col-span-3 flex flex-col items-center justify-center py-8 bg-muted/50 rounded-lg">
                    <Icon name="Camera" size={32} className="text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Aucune photo avant</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-foreground">Photos après intervention</h3>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handlePhotoUpload('after', e)}
                    className="hidden"
                  />
                  <Button variant="outline" size="sm" iconName="Plus" asChild>
                    <span>Ajouter</span>
                  </Button>
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {afterPhotos?.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                    <Image
                      src={photo}
                      alt={`Photo après intervention numéro ${index + 1} montrant les travaux terminés et le problème résolu`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
                {afterPhotos?.length === 0 && (
                  <div className="col-span-3 flex flex-col items-center justify-center py-8 bg-muted/50 rounded-lg">
                    <Icon name="Camera" size={32} className="text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Aucune photo après</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'consumables' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Consommables utilisés</h3>
              <Button variant="outline" size="sm" iconName="Plus">
                Ajouter
              </Button>
            </div>
            {consumables?.map(item => (
              <div key={item?.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{item?.name}</p>
                  <p className="text-xs text-muted-foreground">Unité: {item?.unit}</p>
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min="0"
                    value={item?.quantity}
                    onChange={(e) => handleConsumableChange(item?.id, e?.target?.value)}
                    className="text-center"
                  />
                </div>
              </div>
            ))}
            {consumables?.some(item => item?.quantity > 0) && (
              <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
                <div className="flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} className="text-warning mt-0.5" />
                  <p className="text-xs text-warning">
                    Les consommables utilisés seront automatiquement signalés au dispatch pour réapprovisionnement
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'signature' && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-foreground mb-3">Signature du client</h3>
              <div className="border-2 border-dashed border-border rounded-lg p-4 bg-muted/30">
                {!signature ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Icon name="PenTool" size={48} className="text-muted-foreground mb-4" />
                    <p className="text-sm text-muted-foreground mb-4">
                      Le client doit signer pour confirmer la réalisation des travaux
                    </p>
                    <Button
                      variant="default"
                      iconName="PenTool"
                      onClick={() => setSignature('signed')}
                    >
                      Capturer la signature
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-card rounded-lg p-4 border border-border">
                      <div className="flex items-center justify-center py-8">
                        <Icon name="CheckCircle" size={48} className="text-success" />
                      </div>
                      <p className="text-center text-sm text-muted-foreground">
                        Signature capturée le {new Date()?.toLocaleDateString('fr-FR')} à {new Date()?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      iconName="RefreshCw"
                      onClick={() => setSignature(null)}
                      fullWidth
                    >
                      Recapturer
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={16} className="text-primary mt-0.5" />
                <div className="text-xs text-primary">
                  <p className="font-medium mb-1">Garantie de 6 mois</p>
                  <p>Le client recevra automatiquement le certificat de garantie par email après validation de l'intervention</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex gap-3">
          <Button
            variant="outline"
            iconName="Save"
            className="flex-1"
          >
            Enregistrer
          </Button>
          <Button
            variant="default"
            iconName="CheckCircle"
            onClick={onComplete}
            disabled={!canComplete()}
            className="flex-1"
          >
            Terminer l'intervention
          </Button>
        </div>
        {!canComplete() && (
          <p className="text-xs text-error text-center mt-2">
            Complétez tous les points obligatoires pour terminer
          </p>
        )}
      </div>
    </div>
  );
};

export default InterventionDetails;